/*
* Report page
*/

import * as React from 'react';

import * as ReportComponents from '../report-components';

export const Render = ({ registerFunc, refFunc, scrollToFunc, pageTitle = '', className = '', maxTableRowsPerFirstPage = 0, maxTableRowsPerPage = 0, pageConfiguration, pageCount, reportData, ...rest }) => {
    const sectionName = ReportComponents.ReportSectionNames.glossary;
    const ReportPageWithRegister = ReportComponents.withPageRegistration(ReportComponents.ReportPageHeaderFooter, sectionName, registerFunc, refFunc, scrollToFunc);

    return (
        <div>
            <ReportPageWithRegister pageIndex={0} title={pageTitle} className={className} totalPageCount={pageCount} showFooter={true} footerData={reportData}>
                <div>
                    <div className="pdf-page-glossary-provided-by">(Provided by the National Reserve Study Standards of the Community Associations Institute)</div>

                    <ReportComponents.GlossarySection title="Cash Flow Method">
                        A method of developing a reserve funding plan where contributions to the reserve fund are designed to offset the variable annual
                        expenditures from the reserve fund.  Different reserve funding plans are tested against the anticipated schedule of reserve expenses
                        until the desired funding goal is achieved.
                    </ReportComponents.GlossarySection>

                    <ReportComponents.GlossarySection title="Component">
                        Also referred to as an “Asset.”  Individual line items in the Reserve Study developed or updated in the physical analysis.
                        These elements form the building blocks for the Reserve Study.  Components typically are:  1) Association responsibility, 2)
                        with limited useful life expectancies, 3) have predictable remaining life expectancies, 4) above a minimum threshold cost, and 5)
                        required by local codes.
                    </ReportComponents.GlossarySection>

                    <ReportComponents.GlossarySection title="Component Full Funding">
                        When the actual (or projected) cumulative reserve balance for all components is equal to the fully funded balance.
                    </ReportComponents.GlossarySection>

                    <ReportComponents.GlossarySection title="Component Inventory">
                        The task of selecting and quantifying reserve components.  This task can be
                        accomplished through on-site visual observations, review of association design
                        and organizational  documents, a review of established association precedents, and
                        discussion with appropriate association representatives.
                    </ReportComponents.GlossarySection>

                    <ReportComponents.GlossarySection title="Deficit">
                        An actual (or projected reserve balance), which is less than the fully funded balance.
                    </ReportComponents.GlossarySection>

                    <ReportComponents.GlossarySection title="Effective Age">
                        The difference between useful life and remaining useful life (UL - RUL).
                    </ReportComponents.GlossarySection>

                    <ReportComponents.GlossarySection title="Financial Analysis">
                        The portion of the Reserve Study where current status of the reserves
                        (measured as cash or percent funded) and a recommended reserve contribution
                        rate (reserve funding plan) are derived, and the projected reserve income and
                        expenses over time is presented. The financial analysis is one of the two
                        parts of the Reserve Study.
                    </ReportComponents.GlossarySection>

                    <ReportComponents.GlossarySection title="Fully Funded Balance">
                        An indicator against which the actual (or projected) reserve balance can be compared.
                        The reserve balance that is in direct proportion to the fraction of life “used up” of the current
                        repair or replacement cost of a reserve component.  This number is calculated for each component, and
                        then summed together for an association total.

                        <p>FFB = Current Cost * Effective Age / Useful Life</p>
                    </ReportComponents.GlossarySection>

                    <ReportComponents.GlossarySection title="Fund Status">
                        The status of the reserve fund as compared to an established benchmark, such as percent funded.
                    </ReportComponents.GlossarySection>

                    <ReportComponents.GlossarySection title="Funding Goals">
                        Independent of calculation methodology utilized, the following represent the basic categories of funding plan
                        goals:
                        <ul>
                            <li>Baseline Funding:  Establishing a reserve-funding goal of keeping the reserve balance above zero.</li>
                            <li>Component Full Funding: Setting a reserve funding goal of attaining and maintaining cumulative reserves at or near 100% funded.</li>
                            <li>Threshold Funding:  Establishing a reserve funding goal of keeping the reserve balance above a specified dollar or percent funded amount.</li>
                        </ul>
                    </ReportComponents.GlossarySection>

                    <ReportComponents.GlossarySection title="Funding Plan">
                        An association's plan to provide income to a reserve fund to offset anticipated expenditures from that fund.
                    </ReportComponents.GlossarySection>

                    <ReportComponents.GlossarySection title="Funding Principles">
                        <ul>
                            <li>Sufficient funds when required</li>
                            <li>Stable contributions through the year</li>
                            <li>Evenly distributed contributions over the years</li>
                            <li>Fiscally responsible</li>
                        </ul>
                    </ReportComponents.GlossarySection>

                    <ReportComponents.GlossarySection title="GSF">
                        Gross Square Feet
                    </ReportComponents.GlossarySection>
                </div>
            </ReportPageWithRegister>

            <ReportPageWithRegister pageIndex={1} title="" className={className} totalPageCount={pageCount} showFooter={true} footerData={reportData}>
                <div>
                    <ReportComponents.GlossarySection title="Life and Valuation Estimates">
                        The task of estimating useful life, remaining useful life, and repair or replacement costs for the reserve components.
                    </ReportComponents.GlossarySection>

                    <ReportComponents.GlossarySection title="LF">
                        Linear Feet
                    </ReportComponents.GlossarySection>

                    <ReportComponents.GlossarySection title="Percent Funded">
                        The ratio, at a particular point in time (typically the beginning of the fiscal year), of the actual (or projected)
                        reserve balance to the ideal fund balance, expressed as a percentage.
                    </ReportComponents.GlossarySection>

                    <ReportComponents.GlossarySection title="Physical Analysis">
                        The portion of the Reserve Study where the component evaluation, condition assessment, and life and valuation
                        estimate tasks are performed.  This represents one of the two parts of the Reserve Study.
                    </ReportComponents.GlossarySection>

                    <ReportComponents.GlossarySection title="Remaining Useful Life (RUL)">
                        Also referred to as “remaining life” (RL).  The estimated time, in years, that a reserve component can be
                        expected to continue to serve its intended function.  Projects anticipated to occur in the current fiscal
                        year have a “0” remaining useful life.
                    </ReportComponents.GlossarySection>

                    <ReportComponents.GlossarySection title="Replacement Cost ">
                        The cost of replacing, repairing, or restoring a reserve component to its original functional condition.  The
                        current replacement cost would be the cost to replace, repair, or restore the component during that particular year.
                    </ReportComponents.GlossarySection>

                    <ReportComponents.GlossarySection title="Reserve Balance">
                        Actual or projected funds as of a particular point in time (typically the beginning of the fiscal year) that the
                        association has identified for use to defray the future repair or replacement of those major components that the
                        association is obligated to maintain.  Also known as “reserves,” “reserve accounts,” or “cash reserves.”  In this
                        report the reserve balance is based upon information provided and is not audited.
                    </ReportComponents.GlossarySection>

                    <ReportComponents.GlossarySection title="Reserve Study">
                        A budget-planning tool, which identifies the current status of the reserve fund and a stable and equitable funding
                        plan to offset the anticipated future major common area expenditures.  The Reserve Study consists of two parts:
                        The Physical Analysis and the Financial Analysis.
                    </ReportComponents.GlossarySection>

                    <ReportComponents.GlossarySection title="Special Assessment">
                        An assessment levied on the members of an association in addition to regular assessments.  Governing documents
                        or local statutes often regulate special assessments.
                    </ReportComponents.GlossarySection>

                    <ReportComponents.GlossarySection title="Surplus">
                        An actual (or projected) reserve balance that is greater than the fully funded balance.
                    </ReportComponents.GlossarySection>

                    <ReportComponents.GlossarySection title="Useful Life (UL)">
                        Also known as “life expectancy.”  The estimated time, in years, that a reserve component can be expected to
                        serve its intended function if properly constructed and maintained in its present application of installation.
                    </ReportComponents.GlossarySection>
                </div>
            </ReportPageWithRegister>
        </div>
    );
};



// WEBPACK FOOTER //
// ./src/report/pdf/pages/report-page-glossary.tsx